import React, { useState, useEffect } from 'react';
import './Pages.css';

const ApprovedEndorsements = () => {
  const [endorsements, setEndorsements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEndorsement, setSelectedEndorsement] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchApprovedEndorsements();
  }, []);

  const fetchApprovedEndorsements = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/endorsements/approved');
      const data = await response.json();

      if (data.success) {
        setEndorsements(data.data);
      } else {
        setError('Failed to load approved endorsements');
      }
    } catch (err) {
      console.error('Error fetching endorsements:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewEndorsement = (endorsement) => {
    setSelectedEndorsement(endorsement);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEndorsement(null);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading approved endorsements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Approved Endorsements</h1>
        <span className="badge-count success">{endorsements.length} Approved</span>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="table-container">
        {endorsements.length === 0 ? (
          <div className="empty-state">
            <p>No approved endorsements</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Policy Number</th>
                <th>Client Name</th>
                <th>Registration</th>
                <th>Submitted By</th>
                <th>Date Submitted</th>
                <th>Approved By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {endorsements.map((endorsement, index) => (
                <tr key={endorsement.Id}>
                  <td>{index + 1}</td>
                  <td className="policy-number">{endorsement.PolicyNumber || 'N/A'}</td>
                  <td className="client-name">{endorsement.ClientName || 'N/A'}</td>
                  <td>{endorsement.Registration || 'N/A'}</td>
                  <td>{endorsement.DoneBy || 'N/A'}</td>
                  <td>{formatDate(endorsement.DateDone)}</td>
                  <td>{endorsement.ApprovedBy || 'N/A'}</td>
                  <td>
                    <button 
                      className="action-icon-btn" 
                      title="View Details"
                      onClick={() => handleViewEndorsement(endorsement)}
                    >
                      👁️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View Endorsement Modal */}
      {showModal && selectedEndorsement && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Approved Endorsement</h2>
              <button className="close-modal-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Policy Information */}
              <div className="detail-section">
                <h3>Policy Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Policy Number:</label>
                    <span>{selectedEndorsement.PolicyNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Name:</label>
                    <span>{selectedEndorsement.ClientName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Email:</label>
                    <span>{selectedEndorsement.ClientEmail || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Endorsement Changes */}
              <div className="detail-section">
                <h3>Applied Changes</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Registration:</label>
                    <span>{selectedEndorsement.Registration || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Make:</label>
                    <span>{selectedEndorsement.Make || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Model:</label>
                    <span>{selectedEndorsement.Model || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Chassis Number:</label>
                    <span>{selectedEndorsement.Chassis || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Engine Number:</label>
                    <span>{selectedEndorsement.Engine || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Commencement Date:</label>
                    <span>{formatDate(selectedEndorsement.CommencementDate)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Expiry Date:</label>
                    <span>{formatDate(selectedEndorsement.ExpiryDate)}</span>
                  </div>
                </div>
              </div>

              {/* Approval Info */}
              <div className="detail-section">
                <h3>Approval Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Submitted By:</label>
                    <span>{selectedEndorsement.DoneBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Submitted:</label>
                    <span>{formatDate(selectedEndorsement.DateDone)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Approved By:</label>
                    <span>{selectedEndorsement.ApprovedBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className="status-badge active">Approved</span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="modal-actions">
                <button 
                  className="action-button cancel-endorsement-btn"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedEndorsements;
